import React, { useEffect, useState } from "react";
import DuplicatesPanel from "./DuplicatesPanel";
import { searchEntities } from "./mergeApi";
import { MERGE_TYPES, SELF_EXPERT_SOURCE_TOOLTIP } from "./constants";

const SEARCH_DEBOUNCE_MS = 300;

// "Name (#id)" for a selection with a known label, "#id" as a fallback (a prefilled
// id with no label, or a pick that arrived with a blank name). null when nothing is
// selected yet, so the caller falls back to the in-progress search query.
function selectedDisplayValue(selectedId, selectedLabel) {
  if (!selectedId) return null;
  return selectedLabel ? `${selectedLabel} (#${selectedId})` : `#${selectedId}`;
}

// One typeahead combobox for picking a source or destination entity. Selection is
// controlled entirely by the parent (selectedId/selectedLabel + onSelect/onClear) so
// TypeAndEntityPicker stays the single source of truth for sourceId/destinationId.
// `fieldRole` is "source" or "destination" — not an ARIA role, just which side this is.
function EntityField({ fieldRole, label, type, selectedId, selectedLabel, onSelect, onClear }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (!type || !trimmed) {
      setResults([]);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await searchEntities({ type, q: trimmed });
        if (!cancelled) {
          setResults(data);
          setError(null);
        }
      } catch (err) {
        // Surface the failure: a silently empty dropdown is indistinguishable from
        // "no matches" (the admin access token expiring after an idle hour is the
        // common cause -- a page reload fixes it).
        if (!cancelled) {
          setResults([]);
          setError(err.message || "Search failed");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [type, query]);

  const handleChange = (e) => {
    if (selectedId) onClear();
    setQuery(e.target.value);
  };

  const handleSelect = (entity) => {
    onSelect(entity);
    setQuery("");
    setResults([]);
  };

  const displayValue = selectedDisplayValue(selectedId, selectedLabel) ?? query;
  const showDropdown = !selectedId && query.trim().length > 0 && (loading || error || results.length > 0);

  return (
    <div className="col-md-6 position-relative">
      <label className="form-label fw-bold" htmlFor={`merge-${fieldRole}-id`}>
        {label}
      </label>
      <input
        id={`merge-${fieldRole}-id`}
        type="text"
        className="form-control"
        autoComplete="off"
        value={displayValue}
        onChange={handleChange}
        placeholder="Search by name, slug, or id…"
        data-testid={`merge-${fieldRole}-id`}
      />
      {showDropdown && (
        <ul
          className="list-group position-absolute w-100 shadow-sm"
          style={{ zIndex: 10 }}
          data-testid={`${fieldRole}-search-results`}
        >
          {loading && (
            <li className="list-group-item text-muted" data-testid={`${fieldRole}-search-loading`}>
              Searching…
            </li>
          )}
          {!loading && error && (
            <li className="list-group-item text-danger" data-testid={`${fieldRole}-search-error`}>
              {error} — try reloading the page.
            </li>
          )}
          {!loading &&
            results.map((entity) => {
              const disabled = fieldRole === "source" && entity.self_expert === true;
              return (
                <li key={entity.id} className="list-group-item p-0">
                  <span title={disabled ? SELF_EXPERT_SOURCE_TOOLTIP : undefined} className="d-block">
                    <button
                      type="button"
                      // The admin theme's .btn is inline-flex + justify-content:center,
                      // so text-start alone can't left-align the row.
                      className="btn btn-light w-100 text-start justify-content-start"
                      disabled={disabled}
                      onClick={() => handleSelect(entity)}
                      data-testid={`${fieldRole}-result-${entity.id}`}
                    >
                      {entity.name || `#${entity.id}`} <span className="text-muted">#{entity.id}</span>
                      {entity.slug && <span className="text-muted ms-1">({entity.slug})</span>}
                    </button>
                  </span>
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
}

/**
 * TypeAndEntityPicker — step 1. Choose one of the four entity types, search for the
 * source and destination entities (or fill them from the duplicates helper), then
 * proceed to the preview.
 *
 * sourceLabel/destinationLabel are controlled by the parent (MergeConsole), same as
 * the ids — this component holds no selection state of its own, so a selection made
 * here survives navigating away from and back to this step.
 */
function TypeAndEntityPicker({
  type,
  setType,
  sourceId,
  setSourceId,
  destinationId,
  setDestinationId,
  sourceLabel,
  setSourceLabel,
  destinationLabel,
  setDestinationLabel,
  onPreview,
}) {
  const [showDuplicates, setShowDuplicates] = useState(false);

  const sameEntity = sourceId && destinationId && String(sourceId) === String(destinationId);
  const canPreview = Boolean(type) && Boolean(sourceId) && Boolean(destinationId) && !sameEntity;

  const handlePick = (pickedRole, id, name) => {
    if (pickedRole === "source") {
      setSourceId(String(id));
      setSourceLabel(name || null);
    } else {
      setDestinationId(String(id));
      setDestinationLabel(name || null);
    }
  };

  const handleTypeChange = (value) => {
    setType(value);
    setSourceId("");
    setDestinationId("");
    setSourceLabel(null);
    setDestinationLabel(null);
  };

  const handleResetForm = () => {
    setSourceId("");
    setDestinationId("");
    setSourceLabel(null);
    setDestinationLabel(null);
    setShowDuplicates(false);
  };

  return (
    <div>
      <div className="mb-3">
        <label className="form-label fw-bold" htmlFor="merge-type-select">
          Entity type
        </label>
        <select
          id="merge-type-select"
          className="form-select"
          value={type}
          onChange={(e) => handleTypeChange(e.target.value)}
        >
          <option value="">Select a type…</option>
          {MERGE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      <div className="row g-3">
        <EntityField
          key={`source-${type}`}
          fieldRole="source"
          label={
            <>
              Source <span className="text-danger">(removed)</span>
            </>
          }
          type={type}
          selectedId={sourceId}
          selectedLabel={sourceLabel}
          onSelect={(entity) => handlePick("source", entity.id, entity.name)}
          onClear={() => {
            setSourceId("");
            setSourceLabel(null);
          }}
        />
        <EntityField
          key={`destination-${type}`}
          fieldRole="destination"
          label={
            <>
              Destination <span className="text-success">(kept)</span>
            </>
          }
          type={type}
          selectedId={destinationId}
          selectedLabel={destinationLabel}
          onSelect={(entity) => handlePick("destination", entity.id, entity.name)}
          onClear={() => {
            setDestinationId("");
            setDestinationLabel(null);
          }}
        />
      </div>

      {sameEntity && (
        <div className="alert alert-warning mt-3 mb-0" data-testid="same-entity-warning">
          <i className="fa fa-exclamation-circle me-2" />
          Source and destination must be different entities.
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mt-4">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => setShowDuplicates((v) => !v)}
          disabled={!type}
          data-testid="toggle-duplicates"
        >
          <i className="fa fa-clone me-1" />
          {showDuplicates ? "Hide duplicate finder" : "Find duplicates"}
        </button>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleResetForm}
            data-testid="reset-form-button"
          >
            Reset form
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onPreview}
            disabled={!canPreview}
            data-testid="preview-button"
          >
            Preview merge
          </button>
        </div>
      </div>

      {showDuplicates && type && <DuplicatesPanel type={type} onPick={handlePick} />}
    </div>
  );
}

export default TypeAndEntityPicker;
